import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Upload } from 'lucide-react';
import { MAX_ESCRITURA_FILE_SIZE_BYTES, MAX_ESCRITURA_FILE_SIZE_MB } from '@/common/constants';

interface EscrituraAdicionalUploadFormProps {
  onUpload: (file: File, descripcion: string) => Promise<void>;
  isUploading: boolean;
  isAtLimit: boolean;
}

export const EscrituraAdicionalUploadForm: React.FC<EscrituraAdicionalUploadFormProps> = ({
  onUpload,
  isUploading,
  isAtLimit,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [descripcion, setDescripcion] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_ESCRITURA_FILE_SIZE_BYTES) {
      setError(`El archivo supera el tamaño máximo permitido de ${MAX_ESCRITURA_FILE_SIZE_MB}MB`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setSelectedFile(file);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setError('Seleccioná un archivo PDF');
      return;
    }
    if (!descripcion.trim()) {
      setError('La descripción es obligatoria');
      return;
    }

    try {
      await onUpload(selectedFile, descripcion.trim());
      setDescripcion('');
      setSelectedFile(null);
      setError(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err: any) {
      setError(err.message || 'Error al subir el archivo');
    }
  };

  if (isAtLimit) {
    return (
      <div className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
        Se alcanzó el límite máximo de 100 escrituras adicionales.
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-lg border border-dashed border-gray-300 p-4 bg-gray-50">
      <p className="text-sm font-medium text-gray-700">Agregar escritura adicional</p>

      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          placeholder="Descripción (ej: Testimonio 1ra copia)"
          className="flex-1 text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          maxLength={255}
          disabled={isUploading}
        />

        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="whitespace-nowrap"
          >
            <Upload className="w-4 h-4 mr-1" />
            {selectedFile ? selectedFile.name.slice(0, 20) + (selectedFile.name.length > 20 ? '…' : '') : 'Elegir PDF'}
          </Button>

          <Button
            type="button"
            size="sm"
            onClick={handleSubmit}
            disabled={isUploading || !selectedFile || !descripcion.trim()}
          >
            {isUploading ? 'Subiendo...' : 'Subir'}
          </Button>
        </div>
      </div>

      {error && (
        <p className="text-xs text-red-600">{error}</p>
      )}

      <p className="text-xs text-gray-400">Solo PDF · máx. {MAX_ESCRITURA_FILE_SIZE_MB}MB</p>

      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileSelect}
        className="hidden"
        accept=".pdf"
        disabled={isUploading}
      />
    </div>
  );
};
